/**
 * AnimatedButton — spring button with optional haptic feedback
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useHaptic } from '../../hooks/useHaptic';
import { useSoundEffects, type SoundType } from '../../hooks/useSoundEffects';

type Variant = 'squishy' | 'marshmallow';

type AnimatedButtonProps = Omit<HTMLMotionProps<'button'>, 'ref'> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof HTMLMotionProps<'button'>> & {
    variant?: Variant;
    haptic?: boolean;
    sound?: SoundType;
    glow?: boolean;
  };

const AnimatedButton = forwardRef<HTMLButtonElement, AnimatedButtonProps>(
  ({ variant = 'squishy', haptic = true, sound = 'tap', glow = true, onPointerDown, className, children, ...rest }, ref) => {
    const reduced = useReducedMotion();
    const { tap } = useHaptic();
    const { play } = useSoundEffects();

    const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
      if (haptic) {
        tap();
        play(sound);
      }
      if (glow && e.currentTarget) {
        const rect = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty('--touch-x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
        e.currentTarget.style.setProperty('--touch-y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
      }
      onPointerDown?.(e);
    };

    // Build class list
    const classes = [
      'touch-kid',
      glow && 'touch-glow touch-slop',
      variant === 'marshmallow' && 'btn-marshmallow',
      className,
    ].filter(Boolean).join(' ');

    if (reduced) {
      return (
        <motion.button ref={ref} className={classes} onPointerDown={handlePointerDown} {...rest}>
          {children}
        </motion.button>
      );
    }

    const hoverScale = variant === 'marshmallow' ? 1.03 : 1.05;
    const tapScale = variant === 'marshmallow' ? 0.96 : 0.95;

    return (
      <motion.button
        ref={ref}
        className={classes}
        whileHover={{ scale: hoverScale, y: -2 }}
        whileTap={{ scale: tapScale }}
        transition={springs.snappy}
        onPointerDown={handlePointerDown}
        {...rest}
      >
        {children}
      </motion.button>
    );
  },
);

AnimatedButton.displayName = 'AnimatedButton';
export default AnimatedButton;
